import { useState } from "react";
import { toast } from "sonner";
import { Lightbulb, Plus, Check, Zap, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Btn, inputCls } from "./ui";

const FOCUS = [["mixed", "Cricket + Casino"], ["cricket", "Cricket"], ["casino", "Casino"]];

const Idea = ({ idea, i, isAdded, onAdd, onQueue, busy, queued }) => (
  <li className="flex flex-col gap-2 rounded-xl border border-white/5 bg-black/30 p-3 sm:flex-row sm:items-center" data-testid={`topic-idea-${i}`}>
    <div className="min-w-0 flex-1">
      <p className="text-sm text-white" data-testid={`topic-idea-text-${i}`}>{idea.topic}</p>
      <p className="mt-0.5 text-xs text-zinc-500">
        <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${idea.category === "casino" ? "bg-fuchsia-500/10 text-fuchsia-300" : "bg-emerald-500/10 text-emerald-300"}`}>{idea.category}</span>
        {idea.why}
      </p>
    </div>
    <div className="flex shrink-0 gap-1.5">
      <button type="button" onClick={() => onAdd(idea.topic)} disabled={isAdded} data-testid={`topic-idea-add-${i}`}
        className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/40 px-3 py-1.5 text-[11px] uppercase tracking-wider text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-50">
        {isAdded ? <Check size={12} /> : <Plus size={12} />} {isAdded ? "In list" : "Add"}
      </button>
      <button type="button" onClick={() => onQueue(idea.topic, i)} disabled={busy || queued} data-testid={`topic-idea-queue-${i}`}
        className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-black transition-transform hover:-translate-y-0.5 disabled:opacity-50">
        {queued ? <Check size={12} /> : <Zap size={12} />} {queued ? "Queued" : "Queue"}
      </button>
    </div>
  </li>
);

export const TopicIdeas = ({ language, added, onAdd, onQueue, busy }) => {
  const [focus, setFocus] = useState("mixed");
  const [hint, setHint] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queued, setQueued] = useState({});

  const suggest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/ai/topics", { focus, language, hint, count: 8 }, { timeout: 90000 });
      setIdeas(data.ideas);
      setQueued({});
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const queueOne = async (topic, i) => { if (await onQueue(topic)) setQueued((q) => ({ ...q, [i]: true })); };

  return (
    <div className="mt-6 border-t border-white/5 pt-5" data-testid="topic-ideas">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]"><Lightbulb size={14} /> Trending topic ideas</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-full border border-[#D4AF37]/30 p-0.5">
          {FOCUS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFocus(k)} data-testid={`topic-focus-${k}`}
              className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider ${focus === k ? "bg-[#D4AF37] text-black" : "text-[#D4AF37]"}`}>{l}</button>
          ))}
        </div>
        <input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={200} placeholder="Optional: e.g. India vs Australia series"
          className={`${inputCls} !w-auto min-w-[220px] flex-1 !py-2 text-xs`} data-testid="topic-hint" />
        <Btn variant="outline" onClick={suggest} disabled={loading} data-testid="topic-suggest">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />} {loading ? "Thinking…" : ideas.length ? "More ideas" : "Suggest topics"}
        </Btn>
      </div>
      {ideas.length > 0 && (
        <>
          <ul className="mt-4 space-y-2" data-testid="topic-ideas-list">
            {ideas.map((idea, i) => <Idea key={`${idea.topic}-${i}`} idea={idea} i={i} isAdded={added.includes(idea.topic)} onAdd={onAdd} onQueue={queueOne} busy={busy} queued={queued[i]} />)}
          </ul>
          <button type="button" onClick={() => ideas.forEach((x) => onAdd(x.topic))} data-testid="topic-add-all" className="mt-3 text-xs uppercase tracking-widest text-[#D4AF37] hover:underline">Add all to list</button>
          <p className="mt-1 text-[11px] text-zinc-600">"Queue" schedules it straight away in the next free slot using the settings above.</p>
        </>
      )}
    </div>
  );
};
